'use client';
import React from 'react';
import { Palette, CheckCircle, Info } from 'lucide-react';
import type { OrderState } from './OrderWizardContent';

interface Props {
  order: OrderState;
  updateOrder: (updates: Partial<OrderState>) => void;
  showErrors?: boolean;
}

export default function StepD({ order, updateOrder, showErrors }: Props) {
  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-700 text-white mb-1">Add-ons</h2>
        <p className="text-sm text-slate-400">Optional services to enhance your ad booking.</p>
      </div>

      {/* Design service card */}
      <button
        onClick={() => updateOrder({ addDesign: !order.addDesign })}
        className={`w-full text-left p-6 rounded-2xl border transition-all duration-200 ${
          order.addDesign
            ? 'border-brand-500/60 bg-brand-600/12 shadow-glow-sm'
            : 'border-white/10 bg-navy-700/40 hover:border-brand-500/30 hover:bg-white/3'
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
              order.addDesign ? 'bg-gradient-primary' : 'bg-navy-600 border border-white/10'
            }`}>
              <Palette size={22} className={order.addDesign ? 'text-white' : 'text-slate-400'} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="font-display font-700 text-base text-white">Professional Design Service</p>
                <span className="chip bg-brand-600/20 border-brand-600/30 text-brand-300 text-xs">+$150</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Our design team will create a professional 8.5×5.5 ad for your business. Includes one round of revisions. Final file delivered in print-ready PDF.
              </p>
              <ul className="mt-3 space-y-1.5">
                {[
                  'Custom layout matching your brand',
                  '8.5×5.5 print-ready PDF delivered',
                  'One revision round included',
                  'Turnaround: 3–5 business days',
                ].map((f) => (
                  <li key={`design-feature-${f}`} className="flex items-center gap-2 text-xs text-slate-400">
                    <CheckCircle size={11} className="text-green-400 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${
            order.addDesign ? 'bg-brand-500 border-brand-500' : 'border-white/20'
          }`}>
            {order.addDesign && <CheckCircle size={13} className="text-white" />}
          </div>
        </div>
      </button>

      {/* Not adding design */}
      <div className="mt-4 flex items-start gap-2.5 p-4 rounded-xl bg-blue-500/8 border border-blue-500/20">
        <Info size={14} className="text-blue-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-slate-300 font-medium mb-1">Bringing your own artwork?</p>
          <p className="text-xs text-slate-400">
            Skip this step if you have a ready-to-print file. You'll upload your JPG or PDF (8.5×5.5, 300dpi) in the next step.
            Artwork must be submitted before the issue deadline.
          </p>
        </div>
      </div>
    </div>
  );
}